import React, {useEffect, useState} from 'react';
import {Button, Card, Checkbox, Col, Form, InputNumber, Modal, Row, Select, Slider, Tooltip, Typography} from 'antd';
import {DollarOutlined, InfoCircleOutlined} from '@ant-design/icons';
import Top30InstrumentSelector from '../../pages/trading/components/Top30InstrumentSelector';
import InstrumentDetailPanel from '../../pages/trading/components/InstrumentDetailPanel';
import {estimateContractQuantity} from '../../utils/contractQuantity';
import {formatEffectiveDecimal} from '../../utils/numberFormatter';
import './PocTradingFormCard.css';

const {Text} = Typography;

const DEFAULT_TAKE_PROFIT_PCT = parseFloat((0.8 / 3 * 100).toFixed(2));
const DEFAULT_STOP_LOSS_PCT = parseFloat((0.6 / 3 * 100).toFixed(2));

interface PocTradingFormCardProps {
    apiKeyId: number | null;
}

const PocTradingFormCard: React.FC<PocTradingFormCardProps> = ({apiKeyId}) => {
    const [selectedInstrument, setSelectedInstrument] = useState<string>('');
    const [orderType, setOrderType] = useState<string>('market');
    const [selectedAmount, setSelectedAmount] = useState<number>(20);
    const [fourHourAvgChange, setFourHourAvgChange] = useState<number | null>(null);
    const [currentPrice, setCurrentPrice] = useState<number | null>(null);
    const [limitPrice, setLimitPrice] = useState<number | null>(null);
    const [hasManuallyEditedPrice, setHasManuallyEditedPrice] = useState(false);
    const [confirmVisible, setConfirmVisible] = useState(false);
    const [confirmDirection, setConfirmDirection] = useState<'long' | 'short'>('long');

    const [tpEnabled, setTpEnabled] = useState(true);
    const [tpUsePct, setTpUsePct] = useState(true);
    const [tpPct, setTpPct] = useState<number | null>(DEFAULT_TAKE_PROFIT_PCT);
    const [tpPrice, setTpPrice] = useState<number | null>(null);

    const [slEnabled, setSlEnabled] = useState(true);
    const [slUsePct, setSlUsePct] = useState(true);
    const [slPct, setSlPct] = useState<number | null>(DEFAULT_STOP_LOSS_PCT);
    const [slPrice, setSlPrice] = useState<number | null>(null);

    useEffect(() => {
        if (selectedInstrument) {
            setCurrentPrice(null);
            setLimitPrice(null);
            setHasManuallyEditedPrice(false);
            setTpEnabled(true);
            setSlEnabled(true);
            setTpPct(DEFAULT_TAKE_PROFIT_PCT);
            setSlPct(DEFAULT_STOP_LOSS_PCT);
            setTpPrice(null);
            setSlPrice(null);
            setTpUsePct(true);
            setSlUsePct(true);
            setFourHourAvgChange(null);
        } else {
            setTpEnabled(false);
            setSlEnabled(false);
        }
    }, [selectedInstrument]);

    useEffect(() => {
        if (fourHourAvgChange !== null && fourHourAvgChange !== 0) {
            const tpCalculated = parseFloat((Math.abs(fourHourAvgChange) * 1.2).toFixed(2));
            const slCalculated = parseFloat((Math.abs(fourHourAvgChange) * 1.5).toFixed(2));
            setTpPct(tpCalculated);
            setSlPct(slCalculated);
        }
    }, [fourHourAvgChange, selectedInstrument]);

    const canTrade = selectedInstrument
        && (orderType === 'market' ? !!currentPrice : orderType === 'limit' ? !!limitPrice : true);

    const showConfirm = (direction: 'long' | 'short') => {
        setConfirmDirection(direction);
        setConfirmVisible(true);
    };

    const renderConfirmContent = () => {
        const direction = confirmDirection;
        const orderPrice = orderType === 'limit' && limitPrice ? limitPrice : (currentPrice || 0);
        const lever = 3;
        const notional = selectedAmount * lever;
        const quantity = orderPrice > 0 ? estimateContractQuantity({
            amount: selectedAmount,
            leverage: lever,
            price: orderPrice
        }) : 0;

        let tpPriceCalculated: number | null = null;
        let slPriceCalculated: number | null = null;
        if (tpEnabled && tpPct && orderPrice) {
            tpPriceCalculated = direction === 'long'
                ? orderPrice * (1 + tpPct / 100)
                : orderPrice * (1 - tpPct / 100);
        }
        if (slEnabled && slPct && orderPrice) {
            slPriceCalculated = direction === 'long'
                ? orderPrice * (1 - slPct / 100)
                : orderPrice * (1 + slPct / 100);
        }

        return (
            <div style={{fontSize: '14px', lineHeight: '1.6'}}>
                <div style={{
                    background: 'rgba(7, 10, 18, 0.3)',
                    padding: '16px',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    border: '1px solid #404040'
                }}>
                    <Row justify="space-between" style={{marginBottom: '8px'}}>
                        <Col><Text style={{color: '#a0a0a0'}} strong>合约品种：</Text></Col>
                        <Col><Text style={{color: '#ffffff'}}>{selectedInstrument || '--'}</Text></Col>
                    </Row>
                    <Row justify="space-between" style={{marginBottom: '8px'}}>
                        <Col><Text style={{color: '#a0a0a0'}} strong>交易方向：</Text></Col>
                        <Col><Text style={{color: direction === 'long' ? '#52c41a' : '#ff4d4f'}}>
                            {direction === 'long' ? '开多' : '开空'}
                        </Text></Col>
                    </Row>
                    <Row justify="space-between" style={{marginBottom: '8px'}}>
                        <Col><Text style={{color: '#a0a0a0'}} strong>订单类型：</Text></Col>
                        <Col><Text
                            style={{color: '#ffffff'}}>{orderType === 'market' ? '市价单' : '限价单'}</Text></Col>
                    </Row>
                    <Row justify="space-between" style={{marginBottom: '8px'}}>
                        <Col><Text style={{color: '#a0a0a0'}} strong>委托价格：</Text></Col>
                        <Col><Text style={{color: '#ffffff'}}>
                            {orderType === 'limit' && limitPrice
                                ? `$${limitPrice.toFixed(4)}`
                                : orderPrice
                                    ? `$${orderPrice.toFixed(4)} (市价)`
                                    : '获取中...'
                            }
                        </Text></Col>
                    </Row>
                    <Row justify="space-between" style={{marginBottom: '8px'}}>
                        <Col><Text style={{color: '#a0a0a0'}} strong>保证金：</Text></Col>
                        <Col><Text style={{color: '#ffffff'}}>${selectedAmount}</Text></Col>
                    </Row>
                    <Row justify="space-between" style={{marginBottom: '8px'}}>
                        <Col><Text style={{color: '#a0a0a0'}} strong>杠杆倍数：</Text></Col>
                        <Col><Text style={{color: '#ffffff'}}>{lever}x</Text></Col>
                    </Row>
                    <Row justify="space-between" style={{marginBottom: '8px'}}>
                        <Col><Text style={{color: '#a0a0a0'}} strong>预估数量：</Text></Col>
                        <Col><Text style={{color: '#ffffff'}}>
                            {quantity > 0 ? formatEffectiveDecimal(quantity, 2, 0, 8) : '0'}
                        </Text></Col>
                    </Row>
                    <Row justify="space-between" style={{marginBottom: '8px'}}>
                        <Col><Text style={{color: '#a0a0a0'}} strong>预估名义价值：</Text></Col>
                        <Col><Text style={{color: '#ffffff'}}>${notional}</Text></Col>
                    </Row>

                    {(tpEnabled && (tpPct || tpPrice)) && (
                        <Row justify="space-between" style={{marginBottom: '8px'}}>
                            <Col><Text style={{color: '#a0a0a0'}} strong>止盈价格：</Text></Col>
                            <Col><Text style={{color: '#52c41a'}}>
                                {tpPrice
                                    ? `$${tpPrice.toFixed(4)}`
                                    : tpPriceCalculated
                                        ? `$${tpPriceCalculated.toFixed(4)}`
                                        : '计算中...'
                                }
                            </Text></Col>
                        </Row>
                    )}

                    {(slEnabled && (slPct || slPrice)) && (
                        <Row justify="space-between">
                            <Col><Text style={{color: '#a0a0a0'}} strong>止损价格：</Text></Col>
                            <Col><Text style={{color: '#ff4d4f'}}>
                                {slPrice
                                    ? `$${slPrice.toFixed(4)}`
                                    : slPriceCalculated
                                        ? `$${slPriceCalculated.toFixed(4)}`
                                        : '计算中...'
                                }
                            </Text></Col>
                        </Row>
                    )}
                </div>

                <p style={{
                    color: '#a0a0a0',
                    fontWeight: 'bold',
                    textAlign: 'center',
                    margin: 0
                }}>
                    确认执行合约交易吗？
                </p>
            </div>
        );
    };

    return (
        <Card
            className="poc-trading-form-card"
            title={<><DollarOutlined style={{marginRight: 8}}/> 合约交易</>}
        >
            <div className="poc-trading-form">
                <Form layout="vertical">
                    <Form.Item label={<Text strong>合约品种</Text>}>
                        <Top30InstrumentSelector
                            selectedInstrument={selectedInstrument}
                            onInstrumentChange={setSelectedInstrument}
                            onPriceUpdate={(_, price) => setCurrentPrice(price)}
                            apiKeyId={apiKeyId ?? 0}
                            count={15}
                        />
                    </Form.Item>

                    <InstrumentDetailPanel
                        instId={selectedInstrument}
                        apiKeyId={apiKeyId}
                        onFourHourAvgChange={setFourHourAvgChange}
                        readOnly={true}
                    />

                    <Form.Item label={<Text strong>订单类型</Text>}>
                        <Select
                            value={orderType}
                            onChange={setOrderType}
                            size="large"
                            style={{width: '100%'}}
                        >
                            <Select.Option value="market">市价单</Select.Option>
                            <Select.Option value="limit">限价单</Select.Option>
                        </Select>
                    </Form.Item>

                    {orderType === 'limit' && (
                        <Form.Item label={<><Text strong>委托价格</Text><Tooltip
                            title="限价单必须设置的成交价格"><InfoCircleOutlined
                            style={{marginLeft: 8, color: '#888'}}/></Tooltip></>}>
                            <div style={{position: 'relative'}}>
                                <InputNumber
                                    value={limitPrice}
                                    onChange={(value) => {
                                        setLimitPrice(value);
                                        setHasManuallyEditedPrice(true);
                                    }}
                                    placeholder="输入限价单价格"
                                    size="large"
                                    style={{width: '100%'}}
                                    precision={4}
                                    min={0}
                                    step={0.0001}
                                />
                                {hasManuallyEditedPrice && currentPrice && (
                                    <Button type="link" size="small"
                                            onClick={() => {
                                                setLimitPrice(currentPrice);
                                                setHasManuallyEditedPrice(false);
                                            }}
                                            style={{
                                                position: 'absolute',
                                                right: 8,
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                padding: '0 4px',
                                                height: 'auto',
                                                fontSize: '12px'
                                            }}>
                                        恢复市价
                                    </Button>
                                )}
                            </div>
                        </Form.Item>
                    )}

                    <Form.Item label={<><Text strong>成本金额 (₮)</Text></>}>
                        <div className="poc-amount-selector">
                            <Slider min={5} max={50} step={5} marks={{5: '5', 30: '30', 50: '50'}}
                                    value={selectedAmount} onChange={setSelectedAmount}/>
                        </div>
                    </Form.Item>

                    <Form.Item label={<Text strong>杠杆倍数</Text>}>
                        <div className="poc-lever-display">
                            <Text strong style={{fontSize: '16px'}}>3x</Text>
                            <Text type="secondary" style={{marginLeft: 8}}>固定杠杆</Text>
                        </div>
                    </Form.Item>

                    <Form.Item label={<Text strong>止盈止损设置</Text>}>
                        <div className="poc-profit-loss-settings">
                            <div className="poc-profit-loss-item">
                                <div className="poc-profit-loss-header">
                                    <Checkbox checked={tpEnabled}
                                              onChange={(e) => setTpEnabled(e.target.checked)}><Text>止盈</Text></Checkbox>
                                    <Tooltip title="当价格上涨到指定价格或百分比时自动卖出获利"><InfoCircleOutlined
                                        style={{marginLeft: 4, color: '#888'}}/></Tooltip>
                                </div>
                                {tpEnabled && (
                                    <div className="poc-profit-loss-controls">
                                        <Row gutter={8}>
                                            <Col span={12}><Checkbox checked={tpUsePct} onChange={(e) => {
                                                setTpUsePct(e.target.checked);
                                                if (e.target.checked) setTpPrice(null);
                                            }}>百分比</Checkbox></Col>
                                            <Col span={12}><Checkbox checked={!tpUsePct} onChange={(e) => {
                                                setTpUsePct(!e.target.checked);
                                                if (!e.target.checked) setTpPct(null);
                                            }}>固定价格</Checkbox></Col>
                                        </Row>
                                        <div style={{marginTop: 8}}>
                                            {tpUsePct ? (
                                                <InputNumber value={tpPct} onChange={(v) => setTpPct(v)}
                                                             placeholder="止盈百分比" size="middle"
                                                             style={{width: '100%'}} precision={2} min={0.01}
                                                             step={0.01} formatter={value => `${value}%`}
                                                             parser={value => parseFloat(value!.replace('%', '') || '0')}/>
                                            ) : (
                                                <InputNumber value={tpPrice} onChange={(v) => setTpPrice(v)}
                                                             placeholder="止盈价格" size="middle"
                                                             style={{width: '100%'}} precision={4} min={0}
                                                             step={0.0001}/>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="poc-profit-loss-divider"/>
                            <div className="poc-profit-loss-item">
                                <div className="poc-profit-loss-header">
                                    <Checkbox checked={slEnabled}
                                              onChange={(e) => setSlEnabled(e.target.checked)}><Text>止损</Text></Checkbox>
                                    <Tooltip title="当价格下跌到指定价格或百分比时自动卖出限制亏损"><InfoCircleOutlined
                                        style={{marginLeft: 4, color: '#888'}}/></Tooltip>
                                </div>
                                {slEnabled && (
                                    <div className="poc-profit-loss-controls">
                                        <Row gutter={8}>
                                            <Col span={12}><Checkbox checked={slUsePct} onChange={(e) => {
                                                setSlUsePct(e.target.checked);
                                                if (e.target.checked) setSlPrice(null);
                                            }}>百分比</Checkbox></Col>
                                            <Col span={12}><Checkbox checked={!slUsePct} onChange={(e) => {
                                                setSlUsePct(!e.target.checked);
                                                if (!e.target.checked) setSlPct(null);
                                            }}>固定价格</Checkbox></Col>
                                        </Row>
                                        <div style={{marginTop: 8}}>
                                            {slUsePct ? (
                                                <InputNumber value={slPct} onChange={(v) => setSlPct(v)}
                                                             placeholder="止损百分比" size="middle"
                                                             style={{width: '100%'}} precision={2} min={0.01}
                                                             step={0.01} formatter={value => `${value}%`}
                                                             parser={value => parseFloat(value!.replace('%', '') || '0')}/>
                                            ) : (
                                                <InputNumber value={slPrice} onChange={(v) => setSlPrice(v)}
                                                             placeholder="止损价格" size="middle"
                                                             style={{width: '100%'}} precision={4} min={0}
                                                             step={0.0001}/>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Form.Item>

                    {(() => {
                        const priceForCalc = orderType === 'limit' && limitPrice ? limitPrice : (currentPrice || 0);
                        const quantity = priceForCalc > 0 ? estimateContractQuantity({
                            amount: selectedAmount,
                            leverage: 3,
                            price: priceForCalc
                        }) : 0;
                        const notional = selectedAmount * 3;
                        return selectedInstrument && (
                            <Form.Item label={<><Text strong>预估信息</Text></>}>
                                <div className="poc-estimated-info">
                                    <Row justify="space-between"><Col><Text type="secondary">预估数量：</Text></Col><Col><Text
                                        strong>{quantity > 0 ? formatEffectiveDecimal(quantity, 2, 0, 8) : '0'}</Text></Col></Row>
                                    <Row justify="space-between" style={{marginTop: 4}}><Col><Text
                                        type="secondary">预估名义价值：</Text></Col><Col><Text
                                        strong>${notional.toLocaleString('en-US', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2
                                    })}</Text></Col></Row>
                                    <Row justify="space-between" style={{marginTop: 4}}><Col><Text type="secondary"
                                                                                                   style={{fontSize: '12px'}}>保证金:
                                        ${selectedAmount.toLocaleString('en-US', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2
                                        })}</Text></Col><Col><Text type="secondary" style={{fontSize: '12px'}}>杠杆:
                                        3x</Text></Col></Row>
                                    {currentPrice && (
                                        <Row justify="space-between" style={{marginTop: 4}}><Col><Text type="secondary"
                                                                                                       style={{fontSize: '12px'}}>{orderType === 'limit' && limitPrice ? `委托价格: $${limitPrice.toLocaleString('en-US', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 4
                                        })}` : `当前价格: $${currentPrice.toLocaleString('en-US', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 4
                                        })}`}</Text></Col><Col><Text type="secondary"
                                                                     style={{fontSize: '12px'}}>{orderType === 'limit' ? '限价单' : '市价单'}</Text></Col></Row>
                                    )}
                                </div>
                            </Form.Item>
                        );
                    })()}

                    <Form.Item style={{marginBottom: 0}}>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Button type="primary" size="large" block className="poc-trading-button poc-buy-button"
                                        disabled={!selectedInstrument} onClick={() => showConfirm('long')}>开
                                    多</Button>
                            </Col>
                            <Col span={12}>
                                <Button type="primary" size="large" block className="poc-trading-button poc-sell-button"
                                        disabled={!selectedInstrument} onClick={() => showConfirm('short')}>开
                                    空</Button>
                            </Col>
                        </Row>
                    </Form.Item>
                </Form>
            </div>

            <Modal
                title={<div style={{display: 'flex', alignItems: 'center', gap: '8px'}}><span
                    style={{color: '#ffffff'}}>交易确认</span></div>}
                open={confirmVisible} onCancel={() => setConfirmVisible(false)} width={480} centered
                className="dark-theme-modal"
                footer={<div style={{display: 'flex', justifyContent: 'flex-end', gap: 8}}>
                    <Button onClick={() => setConfirmVisible(false)} className="dark-cancel-btn">取消</Button>
                    <Button type="primary"
                            className={confirmDirection === 'long' ? 'buy-confirm-button' : 'sell-confirm-button'}
                            style={{
                                backgroundColor: confirmDirection === 'long' ? '#52c41a' : '#ff4d4f',
                                borderColor: confirmDirection === 'long' ? '#52c41a' : '#ff4d4f',
                                boxShadow: 'none',
                                color: '#ffffff'
                            }}
                            disabled>确认交易</Button>
                </div>}
            >
                {confirmVisible && renderConfirmContent()}
            </Modal>
        </Card>
    );
};

export default PocTradingFormCard;
